import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  House, Globe, ShoppingCart, ListBullets, Receipt, Gear, SignOut, SunDim, Moon, ArrowsClockwise, CloudArrowUp, TerminalWindow, Funnel, RocketLaunch, Users,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "./ui/button";

const NAV = [
  { to: "/", label: "Overview", icon: House, testid: "nav-overview" },
  { to: "/domains", label: "Domains", icon: Globe, testid: "nav-domains" },
  { to: "/buy", label: "Buy Domains", icon: ShoppingCart, testid: "nav-buy" },
  { to: "/orders", label: "Orders", icon: Receipt, testid: "nav-orders" },
  { to: "/logs", label: "Activity Logs", icon: ListBullets, testid: "nav-logs" },
  { to: "/cloudflare", label: "Cloudflare", icon: CloudArrowUp, testid: "nav-cloudflare" },
  { to: "/cpanel", label: "cPanel", icon: TerminalWindow, testid: "nav-cpanel" },
  { to: "/adspect", label: "Traffic Filter", icon: Funnel, testid: "nav-adspect" },
  { to: "/deploy", label: "Quick Deploy", icon: RocketLaunch, testid: "nav-deploy" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 shrink-0 flex-col border-r border-border bg-card">
        <div className="px-6 pt-6 pb-8 border-b border-border">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="h-9 w-9 rounded-md bg-primary grid place-items-center text-primary-foreground font-display font-black">
              NC
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold tracking-tight text-[15px]">NameOps</div>
              <div className="overline">Domain Manager</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <Icon size={18} weight="regular" />
              <span>{label}</span>
            </NavLink>
          ))}
          {/* Users — super_admin only */}
          {user?.role === "super_admin" && (
            <NavLink
              to="/users"
              data-testid="nav-users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                  isActive
                    ? "bg-purple-500/10 text-purple-400 font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <Users size={18} weight="regular" />
              <span>Users</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-8 w-8 rounded-full bg-muted grid place-items-center text-xs font-semibold">
              {(user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.email}</div>
              <div className="overline">{user?.role}</div>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="h-8 w-8 p-0"
              title="Logout"
            >
              <SignOut size={16} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card/70 backdrop-blur-sm flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <div className="md:hidden font-display font-black">NameOps</div>
            <div className="hidden md:block overline">Namecheap Operations Console</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggle}
              data-testid="theme-toggle"
              className="h-8 w-8 p-0"
              title="Toggle theme"
            >
              {theme === "dark" ? <SunDim size={16} /> : <Moon size={16} />}
            </Button>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1600px] mx-auto w-full">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({ title, description, actions, eyebrow }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
      <div>
        {eyebrow && <div className="overline mb-2">{eyebrow}</div>}
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">{description}</p>
        )}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function RefreshIcon(props) { return <ArrowsClockwise {...props} />; }
